import { type DawdleSeverity } from '@projektarbeit/shared';
import { cn } from '@/lib/utils';

export interface DawdleBadgeProps {
  /** Whole-minute count from the API (already rounded up via Math.ceil). */
  minutes: number;
  /** Pre-classified severity from `classifyDawdle`. */
  severity: DawdleSeverity;
  className?: string;
}

const COLOR_CLASSES: Record<Exclude<DawdleSeverity, 'none'>, string> = {
  green: 'bg-dawdle-green/15 text-dawdle-green border-dawdle-green/30',
  orange: 'bg-dawdle-orange/15 text-dawdle-orange border-dawdle-orange/30',
  red: 'bg-dawdle-red/15 text-dawdle-red border-dawdle-red/30',
};

/**
 * Trödel/dawdle indicator per CLAUDE.md §6.
 *
 * Symbol + Farbe + Text — never colour alone. Renders nothing when there is
 * no episode (`minutes <= 0`) or when severity is `'none'` (avoids cluttering
 * the kiosk overlay with "0 Min unterwegs").
 */
export function DawdleBadge({
  minutes,
  severity,
  className,
}: DawdleBadgeProps): JSX.Element | null {
  if (minutes <= 0 || severity === 'none') return null;
  const text = `${minutes} Min unterwegs`;
  return (
    <span
      role="status"
      aria-label={`Trödelzeit ${minutes} Minuten`}
      className={cn(
        'inline-flex items-center gap-2 rounded-full border px-3 py-1 text-sm font-medium',
        COLOR_CLASSES[severity],
        className,
      )}
      data-severity={severity}
    >
      <span aria-hidden className="text-base">
        ↩
      </span>
      <span>{text}</span>
    </span>
  );
}
